import { Button } from "@/components/ui/button";
import { useQuery } from "@apollo/client";
import { DEFAULT_BANNER, WIDTH_MD } from "@constants/global";
import { useWindowWidth } from "@react-hook/window-size";
import CertificationForm from "components/_Pages/MySpace/Forms/CertificationForm";
import { useModal } from "context/modal/modal.provider";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import { gqlVendor } from "gql";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import useTranslation from "next-translate/useTranslation";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import toast from "react-hot-toast";
import { PRIVATE_ROUTES } from "routes/routes";
import { Swiper, SwiperSlide } from "swiper/react";
import { IVendor } from "types/Vendor.types";
import { useExploreWidgetsStore } from "stores/explore/useExploreWidgetsStore";

interface IBannerInfoPropsProps {
  clean: boolean | undefined;
}

export default function BannerInfo(props: IBannerInfoPropsProps) {
  const { clean } = props;
  const { t } = useTranslation("common");
  const router = useRouter();
  const { openModal } = useModal();
  const { user } = useUserStoreContext((store) => ({
    user: store.user
  }));
  const { banners, setBanners, hasHydrated } = useExploreWidgetsStore();

  const { data: queriedData, loading: bannersLoading } = useQuery<{
    selectedVendor: IVendor;
  }>(gqlVendor.queries.GET_VENDOR_BANNERS, {
    fetchPolicy: "network-only",
    skip: !hasHydrated,
    onCompleted: (data) => {
      setBanners(data);
    }
  });

  const data =
    queriedData ?? (banners.data as { selectedVendor: IVendor } | null);

  const { vendor } = useVendor();
  const { resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";
  const bannerStyle = { minHeight: 300 };
  const wWidth = useWindowWidth();
  const isMd = wWidth >= WIDTH_MD;

  const handleGoToPractice = () => {
    if (user?.role) {
      router.push("/practice");
    } else {
      toast.error("No tienes un rol asignado para iniciar tu desafío diario.");
    }
  };

  const handleCallToAction = (cta: string, modal: string) => {
    if (modal === "AddCertificationModal") {
      openModal({
        backdrop: "static",
        eyebrow: "Certificaciones",
        title: "Nueva certificación",
        bodyClassName: "px-4 py-4 sm:px-5 sm:py-5",
        modalComponent: <CertificationForm />,
        size: "md"
      });

      return;
    }

    if (modal === "GoToMarketplace") {
      router.push(PRIVATE_ROUTES.coinMarketplace.path);
      return;
    }

    if (modal === "GoToMarketplace") {
      router.push(PRIVATE_ROUTES.profile.path);
      return;
    }

    if (modal === "GotToProfileSkills") {
      router.push({
        pathname: PRIVATE_ROUTES.profile.path,
        query: { tab: "endorsement" }
      });
      return;
    }

    if (modal === "GoToProfileCertifications") {
      router.push({
        pathname: PRIVATE_ROUTES.profile.path,
        query: { tab: "certifications" }
      });
      return;
    }

    if (modal === "GoToMyEvaluations") {
      router.push(PRIVATE_ROUTES.myAppreciations.path);
      return;
    }

    if (cta) {
      window.open(cta, "_blank");
    }
  };

  if (bannersLoading && !banners.data) {
    return (
      <div
        className="w-full rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm dark:border-zinc-800 dark:bg-zinc-900"
        role="status"
        aria-label="Loading banner"
      >
        <div className="relative mx-auto mb-4 h-5 w-3/4 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
        </div>
        <div className="relative mx-auto mb-5 h-3 w-2/3 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
        </div>
        <div className="relative mx-auto h-9 w-32 overflow-hidden rounded-xl bg-gray-200 dark:bg-gray-700">
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
        </div>
      </div>
    );
  }

  if (!data?.selectedVendor?.banners?.length && !vendor?.enableChallenges)
    return null;

  return (
    <div className="grid">
      <div className="relative overflow-hidden rounded-xl">
        <Swiper
          spaceBetween={50}
          className="max-w-full"
          pagination={{ clickable: true, enabled: true }}
          navigation={false}
          autoplay
        >
          {isMd &&
            data?.selectedVendor?.banners?.map(
              ({
                bannerUrl,
                title,
                description,
                btnText,
                btnColor,
                cta,
                modal
              }) => (
                <SwiperSlide key={bannerUrl} style={bannerStyle}>
                  <div className="absolute left-0 top-0 z-0 h-full w-full rounded-xl">
                    <Image
                      src={bannerUrl || DEFAULT_BANNER}
                      objectFit="cover"
                      layout="fill"
                      className="w-full rounded-xl"
                      alt="banner background "
                      priority
                    />
                  </div>
                  <div
                    className={
                      "absolute left-0 top-0 z-7 flex h-full w-full flex-col items-center justify-center gap-2 rounded-md bg-gradient-to-t from-black to-transparent dark:from-black"
                    }
                  >
                    <span className="rounded-lg p-1 text-center text-lg font-bold text-white">
                      {title}
                    </span>
                    <p className="rounded-lg p-2 text-center text-white">
                      {description}
                    </p>
                    {btnText &&
                      ((cta && cta !== "https://undefined") || modal) && (
                        <div>
                          <Button
                            className="text-xs"
                            onClick={() =>
                              handleCallToAction(cta ?? "", modal ?? "")
                            }
                            style={{ backgroundColor: btnColor }}
                          >
                            {btnText}
                          </Button>
                        </div>
                      )}
                  </div>
                </SwiperSlide>
              )
            )}
          {vendor?.enableChallenges && (
            <SwiperSlide key="defaultSlide" style={bannerStyle}>
              <div
                className={cn(
                  "absolute left-0 top-0 z-0 h-full w-full overflow-hidden rounded-xl",
                  isDarkTheme
                    ? "bg-zinc-950"
                    : "border border-slate-200/70 bg-white shadow-sm"
                )}
              >
                <div className="absolute top-0 h-full w-full">
                  <Image
                    src="https://cdn.ndrz.io/groowcity/general/banner-bg-clean-opt.png"
                    objectFit="cover"
                    layout="fill"
                    className={cn(
                      "w-full rounded-xl transition-opacity duration-300",
                      isDarkTheme ? "opacity-100" : "opacity-0"
                    )}
                    alt="banner"
                  />
                </div>
                {clean && (
                  <div
                    className={cn(
                      "absolute top-0 flex h-full w-full flex-col items-center justify-center gap-3 p-4 font-bold",
                      isDarkTheme
                        ? "bg-gradient-to-t from-black/65 via-black/20 to-transparent"
                        : "bg-[radial-gradient(circle_at_50%_22%,rgba(99,102,241,0.045),transparent_42%),radial-gradient(circle_at_12%_10%,rgba(14,165,233,0.035),transparent_30%),radial-gradient(circle_at_88%_12%,rgba(236,72,153,0.028),transparent_30%)]"
                    )}
                  >
                    <span
                      className={cn(
                        "mt-0 text-center text-lg font-bold",
                        isDarkTheme ? "text-white" : "text-slate-900"
                      )}
                    >
                      {t("Comienza tu desafío diario")}
                    </span>
                    <span
                      className={cn(
                        "text-center text-sm font-light",
                        isDarkTheme ? "text-white" : "text-slate-600"
                      )}
                    >
                      {t("y conviértete en el")}{" "}
                      <b
                        className={cn(
                          "font-semibold",
                          isDarkTheme ? "text-yellow-400" : "text-indigo-600"
                        )}
                      >
                        #1
                      </b>{" "}
                      {t("de")} {t("tu organización")}
                    </span>
                    <Button
                      onClick={handleGoToPractice}
                      className="bg-indigo-600 text-xs text-white hover:bg-indigo-500"
                    >
                      {t("Comienza ahora")}
                    </Button>
                  </div>
                )}
              </div>
            </SwiperSlide>
          )}
        </Swiper>
      </div>
    </div>
  );
}
